import { useEffect, useState } from 'react';
import { MONO, Titre, Bouton } from '../composants/base.jsx';
import { Feuille } from '../composants/Feuille.jsx';
import { Vers } from '../composants/icones.jsx';
import { Terrain } from '../composants/Terrain.jsx';
import { supprimerMatch, fileEnAttente, vidangerFile } from '../local/depot.js';
import { FORMATIONS } from '../data/formations.js';
import { LIBELLES, score as scoreDe } from '../logique/match.js';
import { besoinMiseAJour } from '../logique/fraicheur.js';

const ETIQUETTE = { probable: 'Probable', annoncee: 'Annoncée', jouee: null };

/**
 * Ce que tu as suivi, et ce que le coach aligne vraiment.
 *
 * Un match y est une ligne : date, adversaire, score, formation. Le toucher ouvre
 * tout ce qu'il contient — le onze sur un terrain, et le déroulé. Il n'y avait avant
 * que des compositions sans leur match, et le journal se consultait depuis deux
 * endroits différents ; il ne se consulte plus que d'ici et du bandeau de match.
 */
export function EcranMemoire({ instantane, matchs = [], historique = [], brouillon, onOuvrir, onChangement, onPreparer }) {
  const [etat, setEtat] = useState(null);
  const [enAttente, setEnAttente] = useState(0);
  const [ouvert, setOuvert] = useState(null);

  useEffect(() => {
    fileEnAttente().then(setEnAttente).catch(() => {});
    fetch('/api/sante', { signal: AbortSignal.timeout(5000) })
      .then((r) => (r.ok ? r.json() : null)).then(setEtat).catch(() => setEtat(null));
  }, [matchs.length]);

  const effectif = instantane?.effectif ?? [];
  const parId = new Map(effectif.map((j) => [j.id, j]));

  /**
   * LA SAISON, journée par journée — le calendrier et tes matchs dans une seule liste.
   *
   * Ils vivaient dans deux sections distinctes, si bien qu'une journée jouée sans que
   * tu l'aies suivie n'apparaissait nulle part, et qu'une journée à venir non plus.
   * Fusionnées, la liste répond à la seule question qu'on lui pose : où en est la
   * saison, et qu'est-ce que j'ai suivi.
   */
  const journees = (instantane?.calendrier?.journees ?? []).map((j) => {
    const mien = matchs.find((m) => memeAdversaire(m.adversaire, j.adversaire) && m.date === j.date)
      ?? matchs.find((m) => memeAdversaire(m.adversaire, j.adversaire));
    // Un onze relevé à la main pour une journée que tu n'as pas suivie : il alimente
    // le pré-remplissage, mais il n'est pas de toi et l'écran doit le dire.
    const releve = !mien && historique.some((h) => h.origine === 'releve' && h.date === j.date);
    // La journée en cours de préparation : elle n'est pas encore un match, mais elle
    // n'est pas rien non plus, et la toucher doit y revenir plutôt que la recommencer.
    const enCours = !mien && memeAdversaire(brouillon?.adversaire, j.adversaire);
    return { ...j, mien, releve, enCours };
  });
  // Les matchs sans journée correspondante (amical, coupe) ne doivent pas disparaître.
  const horsCalendrier = matchs.filter((m) => !journees.some((j) => j.mien?.id === m.id));

  const besoin = besoinMiseAJour(instantane ?? {});
  const aVenir = journees.filter((j) => !j.score).slice(0, 3);
  const jouees = journees.filter((j) => j.score).reverse();

  const bilan = {};
  for (const h of historique) if (h.formation) bilan[h.formation] = (bilan[h.formation] ?? 0) + 1;

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <header style={{ flex: 'none', padding: 'calc(env(safe-area-inset-top) + 12px) 18px 12px' }}>
        <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, letterSpacing: '-.5px' }}>Mémoire</h1>
        <div style={{ fontSize: 11, fontFamily: MONO, color: 'var(--ink2)', marginTop: 4 }}>
          {matchs.length} match{matchs.length > 1 ? 's' : ''} suivi{matchs.length > 1 ? 's' : ''}
        </div>
      </header>

      <div className="defile" style={{ flex: 1, minHeight: 0, padding: '0 14px 20px' }}>

        {/* L'AVERTISSEMENT NE PARAÎT QUE QUAND IL SERT.
            Un bandeau permanent devient du décor : on cesse de le lire au bout de
            trois jours, et il ne sert plus le jour où il compte. Les règles sont dans
            src/logique/fraicheur.js, et elles sont étroites — sept joueurs sans poste
            précis n'en déclenchent aucun, parce que rien n'est cassé pour autant. */}
        {besoin.niveau && (
          <div style={{
            marginBottom: 14, padding: '12px 14px', borderRadius: 14,
            background: besoin.niveau === 'necessaire' ? 'color-mix(in oklch, var(--warn) 14%, transparent)' : 'var(--surf)',
            border: `1px solid ${besoin.niveau === 'necessaire' ? 'var(--warn)' : 'var(--line)'}`,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: besoin.niveau === 'necessaire' ? 'var(--warn)' : 'var(--ink3)' }} />
              <span style={{ fontSize: 12.5, fontWeight: 700, color: besoin.niveau === 'necessaire' ? 'var(--warn)' : 'var(--ink2)' }}>
                {besoin.niveau === 'necessaire' ? 'Mise à jour nécessaire' : 'Mise à jour utile'}
              </span>
            </div>
            {besoin.raisons.map((r) => (
              <div key={r.code} style={{ fontSize: 12, lineHeight: 1.5, color: 'var(--ink2)' }}>· {r.texte}</div>
            ))}
            <div style={{ marginTop: 9, fontSize: 11.5, color: 'var(--ink3)', lineHeight: 1.5 }}>
              Sur le serveur : <code style={{ fontFamily: MONO, color: 'var(--ink)', background: 'var(--surf2)', padding: '2px 6px', borderRadius: 5 }}>aja</code>
            </div>
          </div>
        )}

        {aVenir.length > 0 && (
          <>
            <Titre style={{ padding: '0 4px 9px' }}>À VENIR</Titre>
            {aVenir.map((j) => (
              <button
                key={j.journee}
                onClick={() => (j.mien ? setOuvert(j.mien) : onPreparer?.(j))}
                style={{ display: 'flex', alignItems: 'center', gap: 11, width: '100%', textAlign: 'left', padding: 11, marginBottom: 6, borderRadius: 14, background: 'var(--surf)', border: '1px solid var(--line)' }}
              >
                <Lieu domicile={j.domicile} />
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 13.5, fontWeight: 600 }}>{affiche(j)}</span>
                  <span style={{ display: 'block', fontSize: 10, fontFamily: MONO, color: 'var(--ink2)', marginTop: 2 }}>
                    J{j.journee} · {dateCourte(j.date)} · <span style={{ color: 'var(--acc)', fontWeight: 700 }}>{echeance(j.date)}</span>
                    {j.aConfirmer ? ' · à confirmer' : ''}
                  </span>
                </span>
                <span style={{ flex: 'none', fontSize: 11.5, fontWeight: 700, color: j.mien ? 'var(--acc2)' : 'var(--acc)' }}>
                  {j.mien ? 'Compo posée'
                    : j.enCours ? `${brouillon.places.filter((p) => p.joueurId).length}/11 · reprendre`
                    : 'Préparer'}
                </span>
              </button>
            ))}
          </>
        )}

        <Titre style={{ padding: '22px 4px 9px' }}>LA SAISON</Titre>
        {jouees.length === 0 && horsCalendrier.length === 0 && (
          <Vide>Aucune journée jouée pour l&apos;instant.</Vide>
        )}
        {jouees.map((j) => (
          <LigneJournee
            key={j.journee}
            journee={j}
            onOuvrir={() => (j.mien ? setOuvert(j.mien) : onPreparer?.(j))}
          />
        ))}
        {horsCalendrier.map((m) => (
          <LigneJournee
            key={m.id}
            journee={{ date: m.date, adversaire: m.adversaire, domicile: m.domicile, score: scoreTexte(m), mien: m }}
            onOuvrir={() => setOuvert(m)}
          />
        ))}

        {Object.keys(bilan).length > 1 && (
          <div style={{ marginTop: 8, padding: '11px 13px', borderRadius: 13, background: 'var(--surf2)', border: '1px solid var(--line)', fontSize: 11.5, color: 'var(--ink2)' }}>
            {Object.entries(bilan).sort((a, b) => b[1] - a[1]).map(([f, n]) => `${f} ×${n}`).join(' · ')}
          </div>
        )}

        <Titre style={{ padding: '22px 4px 9px' }}>ÉTAT</Titre>
        <div style={{ padding: '13px 14px', borderRadius: 14, background: 'var(--surf)', border: '1px solid var(--line)', fontSize: 12, lineHeight: 1.7, color: 'var(--ink2)' }}>
          <Ligne nom="Effectif" valeur={etat ? (etat.fournisseur === 'local' ? 'relevé local' : etat.fournisseur) : 'serveur injoignable'} />
          {etat?.quota && <Ligne nom="Quota du jour" valeur={`${etat.quota.restantes} requêtes restantes`} />}
          <Ligne nom="Écritures en attente" valeur={enAttente === 0 ? 'aucune' : `${enAttente} — repartiront au réseau`} />
          {enAttente > 0 && (
            <div style={{ marginTop: 9 }}>
              <Bouton ton="neutre" taille={40} onClick={async () => { await vidangerFile().catch(() => {}); setEnAttente(await fileEnAttente()); }}>Envoyer maintenant</Bouton>
            </div>
          )}
        </div>

        {/* Hors du tiers du pouce, et en bas de l'onglet le moins fréquenté : se
            déconnecter oblige à retaper le mot de passe, et le stade n'a pas de réseau. */}
        <button
          onClick={async () => {
            if (!confirm('Se déconnecter ? Il faudra retaper le mot de passe, et le stade n’a pas de réseau.')) return;
            await fetch('/api/deconnexion', { method: 'POST' }).catch(() => {});
            location.reload();
          }}
          style={{ display: 'block', width: '100%', marginTop: 18, padding: '13px 0', fontSize: 12.5, fontWeight: 600, color: 'var(--ink3)' }}
        >
          Se déconnecter
        </button>
      </div>

      <DetailMatch
        match={ouvert}
        parId={parId}
        effectif={effectif}
        onFermer={() => setOuvert(null)}
        onOuvrir={() => { const m = ouvert; setOuvert(null); onOuvrir?.(m); }}
        onSupprimer={async () => { await supprimerMatch(ouvert.id); setOuvert(null); onChangement?.(); }}
      />
    </div>
  );
}

/** Le détail : le onze sur un terrain, puis le déroulé. Les deux d'un seul coup d'œil. */
function DetailMatch({ match, parId, effectif, onFermer, onOuvrir, onSupprimer }) {
  if (!match) return null;
  const table = FORMATIONS[match.formation] ?? [];
  const places = table.map((c, i) => ({
    index: i, code: c.code, x: c.x, y: c.y,
    joueurId: (match.places ?? []).find((p) => p.index === i)?.joueurId ?? null,
  }));
  const s = scoreDe({ evenements: match.evenements ?? [] });
  // Le libellé dit ce qu'on reprend vraiment. « Reprendre ce onze » devant un terrain
  // vide est un mensonge poli : on ne reprendrait que la formation.
  const poses = places.filter((p) => p.joueurId).length;
  const libelle = poses === places.length ? 'Reprendre ce onze'
    : poses === 0 ? 'Reprendre la formation'
    : `Reprendre ces ${poses} joueurs`;

  return (
    <Feuille
      ouverte
      onFermer={onFermer}
      titre={titre(match)}
      sousTitre={`${dateCourte(match.date)} · ${match.formation}${match.etat === 'jouee' && s ? ` · ${s.nous}–${s.eux}` : ''}`}
    >
      <div style={{ padding: '0 14px 22px' }}>
        {poses === 0 && (
          <div style={{ marginBottom: 10, padding: '10px 12px', borderRadius: 11, background: 'var(--surf2)', fontSize: 12, lineHeight: 1.45, color: 'var(--ink2)' }}>
            Aucun joueur n&apos;a été posé sur ce match — seule la formation {match.formation} est enregistrée.
          </div>
        )}

        <Terrain
          style={{ height: 300, marginBottom: 14 }}
          places={places}
          effectif={effectif}
          retourne={false}
          cibles={[]}
        />

        {(match.evenements ?? []).length > 0 && (
          <>
            <Titre style={{ padding: '4px 4px 6px' }}>DÉROULÉ</Titre>
            {match.evenements.map((e) => (
              <div key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '9px 4px', borderBottom: '1px solid var(--surf2)' }}>
                <span style={{ width: 32, fontFamily: MONO, fontWeight: 700, fontSize: 12 }}>{e.minute}&apos;</span>
                <span style={{ flex: 1, minWidth: 0, fontSize: 12.5 }}>
                  <span style={{ fontWeight: 600 }}>{LIBELLES[e.type] ?? e.type}</span>
                  {e.type === 'chg'
                    ? <span style={{ color: 'var(--ink2)', display: 'inline-flex', alignItems: 'center', gap: 4 }}> · {parId.get(e.sortId)?.nom ?? '?'} <Vers t={12} /> {parId.get(e.entreId)?.nom ?? '?'}</span>
                    : e.joueurId ? <span style={{ color: 'var(--ink2)' }}> · {parId.get(e.joueurId)?.nom ?? '?'}</span> : null}
                </span>
              </div>
            ))}
          </>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
          {/* « Reprendre » et non « Ouvrir » : on copie dans la préparation du prochain
              match, on ne rouvre pas l'archive. Un match joué sert d'historique au
              pré-remplissage ; le modifier après coup reviendrait à réécrire le passé. */}
          <Bouton ton="principal" large taille={48} onClick={onOuvrir}>{libelle}</Bouton>
          <Bouton ton="danger" taille={48} style={{ width: 48, padding: 0 }} aria-label="Supprimer ce match"
            onClick={() => { if (confirm('Supprimer ce match ? Son onze ne servira plus au pré-remplissage.')) onSupprimer(); }}>×</Bouton>
        </div>
      </div>
    </Feuille>
  );
}

/**
 * Le lieu, d'un coup d'œil.
 *
 * « domicile » écrit en toutes lettres dans une ligne de métadonnées se lit en
 * dernier, alors que c'est la première chose qui change la lecture d'un résultat :
 * un nul à l'extérieur et un nul à domicile ne racontent pas la même histoire.
 */
const Lieu = ({ domicile }) => (
  <span
    aria-label={domicile ? 'à domicile' : "à l'extérieur"}
    style={{
      width: 26, height: 38, flex: 'none', borderRadius: 8,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 9.5, fontWeight: 800, letterSpacing: '.5px',
      background: domicile ? 'var(--acc)' : 'transparent',
      color: domicile ? 'var(--tokink)' : 'var(--ink2)',
      border: domicile ? '1px solid transparent' : '1px solid var(--line)',
    }}
  >{domicile ? 'DOM' : 'EXT'}</span>
);

function LigneJournee({ journee: j, onOuvrir }) {
  const suivi = !!j.mien;
  return (
    <button
      onClick={onOuvrir}
      style={{
        display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left',
        padding: 11, marginBottom: 6, borderRadius: 14,
        background: 'var(--surf)', border: '1px solid var(--line)',
        opacity: suivi ? 1 : .72,
      }}
    >
      <Lieu domicile={j.domicile} />
      <span style={{
        width: 46, flex: 'none', textAlign: 'center',
        fontFamily: MONO, fontWeight: 800, fontSize: 15,
        color: couleurScore(j.score, j.domicile),
      }}>{j.score ?? '—'}</span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 13.5, fontWeight: 600 }}>{j.adversaire ?? 'Adversaire inconnu'}</span>
        <span style={{ display: 'block', fontSize: 10, fontFamily: MONO, color: 'var(--ink2)', marginTop: 2 }}>
          {j.journee ? `J${j.journee} · ` : ''}{dateCourte(j.date)}
        </span>
      </span>
      {/* TROIS ÉTATS DISTINCTS, et la distinction compte.
          « Ton onze » est ce que tu as affirmé ; « relevé » a été saisi pour toi et
          sert quand même au pré-remplissage ; « rien saisi » dit franchement qu'il n'y
          a pas de donnée, plutôt que de laisser croire à un blanc. Les journées sans
          rien restent touchables : on peut poser le onze après coup. */}
      {suivi ? (
        <span style={{ flex: 'none', padding: '5px 9px', borderRadius: 8, background: 'var(--surf2)', fontFamily: MONO, fontWeight: 700, fontSize: 11 }}>{j.mien.formation}</span>
      ) : j.releve ? (
        <span style={{ flex: 'none', padding: '5px 9px', borderRadius: 8, border: '1px solid var(--line)', fontSize: 10, fontWeight: 700, color: 'var(--ink2)' }}>relevé</span>
      ) : (
        <span style={{ flex: 'none', fontSize: 10.5, fontWeight: 600, color: 'var(--acc)' }}>rien saisi</span>
      )}
    </button>
  );
}

// Le score est écrit du point de vue de l'AJA : « 2-1 » veut dire qu'elle a marqué
// deux fois, que le match ait été joué chez elle ou non.
function couleurScore(score) {
  if (!score) return 'var(--ink3)';
  const [nous, eux] = String(score).split('-').map(Number);
  if (!Number.isFinite(nous) || !Number.isFinite(eux)) return 'var(--ink2)';
  if (nous > eux) return 'var(--acc2)';
  if (nous < eux) return 'var(--bad)';
  return 'var(--ink2)';
}

function scoreTexte(m) {
  const s = scoreDe({ evenements: m.evenements ?? [] });
  return m.etat === 'jouee' && (s.nous || s.eux) ? `${s.nous}-${s.eux}` : null;
}

const affiche = (j) => (j.domicile ? `AJA — ${j.adversaire}` : `${j.adversaire} — AJA`);

// Le nom de l'adversaire est saisi à la main dans « Nouveau match » : il peut porter
// une majuscule ou un espace de plus que le calendrier.
const memeAdversaire = (a, b) => !!a && !!b
  && String(a).trim().toLowerCase() === String(b).trim().toLowerCase();

const titre = (m) => (m.adversaire ? (m.domicile === false ? `${m.adversaire} — AJA` : `AJA — ${m.adversaire}`) : 'Match sans adversaire');

const Ligne = ({ nom, valeur }) => (
  <div style={{ display: 'flex', gap: 10 }}>
    <span style={{ flex: 1, minWidth: 0 }}>{nom}</span>
    <span style={{ color: 'var(--ink)', fontWeight: 600, textAlign: 'right' }}>{valeur}</span>
  </div>
);

const Vide = ({ children }) => (
  <div style={{ padding: '16px 6px', fontSize: 12.5, lineHeight: 1.5, color: 'var(--ink3)' }}>{children}</div>
);

function couleurResultat(s) {
  if (!s) return 'var(--ink2)';
  if (s.nous > s.eux) return 'var(--acc2)';
  if (s.nous < s.eux) return 'var(--bad)';
  return 'var(--ink2)';
}

/**
 * Dans combien de temps.
 *
 * « 11/10 » demande de calculer de tête ; « dans 21 jours » répond directement à la
 * seule question qu'on pose à une date future. Et le jour même, c'est la seule chose
 * qui compte.
 */
function echeance(iso) {
  try {
    const cible = new Date(`${iso}T00:00:00`);
    const auj = new Date(new Date().toISOString().slice(0, 10) + 'T00:00:00');
    const j = Math.round((cible - auj) / 86400000);
    if (j === 0) return "aujourd'hui";
    if (j === 1) return 'demain';
    if (j < 0) return `il y a ${-j} j`;
    if (j < 7) return `dans ${j} jours`;
    if (j < 14) return 'dans 1 semaine';
    return `dans ${Math.round(j / 7)} semaines`;
  } catch { return ''; }
}

const dateCourte = (iso) => {
  try { return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' }); }
  catch { return ''; }
};
